import { PerimeterSearchContext } from "@App/contexts/apis/PerimeterSearchContext";
import { useGaragesRequestFromStore } from "@App/hooks/api/graphql/perimeterSearch/requests/factories/useGaragesRequestFromStore";
import type { State } from "@App/state";
import type { Garage } from "@App/types";
import { SortOrderType } from "@App/types/enums";
import { OrganizationIsAthlonAndSortByIsPartnerRecommended } from "@App/utils/conditionals/combinations";
import { getConfig } from "@App/utils/getConfig";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

/**
 * Custom hook to fetch and manage a list of recommended garages.
 *
 * @returns {Object} - The state and methods for managing the recommended garages.
 * @property {boolean} loading - Indicates whether the recommendation process is in progress.
 * @property {Garage[] | null} recommendedGarages - The list of recommended garages, or null if not available.
 * @property {Error | null} error - Any error encountered during the recommendation process, or null if none.
 */
export const useRecommendedGarages = (): {
  loading: boolean;
  recommendedGarages: Garage[] | null;
  error: Error | null;
} => {
  const perimeterSearchApi = useContext(PerimeterSearchContext);

  // Retrieve necessary state values from the Redux store.
  const organization = useSelector((state: State) => state.orders.organization);
  const search = useSelector((state: State) => state.filters.search);
  const sortBy = useSelector((state: State) => state.filters.sortBy);

  // Get the maximum number of recommended garages from the configuration.
  const maxRecommendedGarages = getConfig(organization).garageList?.maxRecommendedGarages ?? 20;

  // State variables to manage loading, errors, and the list of recommended garages.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [recommendedGarages, setRecommendedGarages] = useState<Garage[]>([]);

  // Generate the request object for the perimeter search API.
  const request = useGaragesRequestFromStore();

  /**
   * Fetches the list of recommended garages from the API.
   *
   * @returns {Promise<Garage[]>} - A promise that resolves to the list of recommended garages.
   */
  const fetchRecommendedGarages = async (): Promise<Garage[]> => {
    if (!request) return [];
    const response = await perimeterSearchApi.getGarages({
      ...request,
      onlyPreferredPartners: true,
      offset: 0,
      limit: maxRecommendedGarages,
      sortBy: SortOrderType.SMALLEST_DISTANCE,
    });
    return response.data;
  };

  /**
   * Effect to load recommended garages when the request, search, or sortBy state changes.
   * Clears the list if the request is invalid or the search is active.
   */
  useEffect(() => {
    if (!request || search) {
      setRecommendedGarages([]);
      return;
    }

    const loadRecommendations = async () => {
      try {
        setLoading(true);
        const garages = await fetchRecommendedGarages();
        setRecommendedGarages(garages);
      } catch (e) {
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    };

    const shouldFetchRecommended =
      sortBy === SortOrderType.RECOMMENDED ||
      OrganizationIsAthlonAndSortByIsPartnerRecommended(organization, sortBy);

    if (!shouldFetchRecommended) {
      setRecommendedGarages([]);
      return;
    }

    loadRecommendations();
  }, [request, search, sortBy]);

  // Return the state and methods for external use.
  return { loading, recommendedGarages, error };
};
